import { useState, useEffect } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, User, LogOut, LayoutDashboard, Facebook, Instagram } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

const navLinks = [
  { to: "/services", label: "Services & Pricing" },
  { to: "/airbnb-turnover", label: "Airbnb Turnover" },
  { to: "/residential", label: "Residential" },
  { to: "/deep-cleaning", label: "Deep Cleaning" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [social, setSocial] = useState({ facebook_url: "", instagram_url: "" });
  useEffect(() => { api.get("/settings/public").then((r) => setSocial({ facebook_url: r.data.facebook_url || "", instagram_url: r.data.instagram_url || "" })).catch(() => {}); }, []);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const doLogout = async () => {
    await logout();
    setOpen(false);
    navigate("/");
  };

  return (
    <header className="sticky top-0 z-50 bg-[#FBF9F5]/90 backdrop-blur-md border-b border-[#B87333]/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="header-logo">
            <img src="/masaya-logo.png" alt="Masaya Hospitality Cleaning" className="h-14 w-auto" />
          </Link>

          <nav className="hidden lg:flex items-center gap-6">
            {navLinks.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                className={({ isActive }) =>
                  `text-sm font-medium transition-colors ${isActive ? "text-[#B87333]" : "text-[#635B52] hover:text-[#2C2621]"}`
                }
                data-testid={`nav-${l.to.slice(1)}`}
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            {user && user.role === "admin" && (
              <Link to="/admin" className="text-sm font-medium text-[#2C2621] hover:text-[#B87333] flex items-center gap-1" data-testid="header-admin-link">
                <LayoutDashboard size={16} /> Owner
              </Link>
            )}
            {user ? (
              <>
                <Link to="/portal" className="border border-[#B87333]/40 hover:border-[#B87333] text-[#2C2621] hover:bg-[#F5F0E8] font-medium rounded-full px-4 py-2 text-sm flex items-center gap-1 transition-all" data-testid="header-portal-btn">
                  <User size={15} /> My Account
                </Link>
                <button onClick={doLogout} className="text-[#635B52] hover:text-[#2C2621] p-2" title="Log out" data-testid="header-logout-btn">
                  <LogOut size={18} />
                </button>
              </>
            ) : (
              <Link to="/login" className="border border-[#B87333]/40 hover:border-[#B87333] text-[#2C2621] hover:bg-[#F5F0E8] font-medium rounded-full px-4 py-2 text-sm transition-all" data-testid="header-login-btn">
                Client Login
              </Link>
            )}
            <Link to="/get-estimate" className="bg-[#2C2621] hover:bg-[#1E1A17] text-white font-medium rounded-full px-4 py-2 text-sm shadow-sm transition-all" data-testid="header-estimate-btn">
              Get a Quote
            </Link>
            <Link to="/book" className="bg-[#B87333] hover:bg-[#A06127] text-white font-medium rounded-full px-5 py-2.5 text-sm shadow-md hover:shadow-lg active:scale-95 transition-all" data-testid="header-book-btn">
              Book Now
            </Link>
          </div>

          <button className="lg:hidden text-[#2C2621] p-2" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle" aria-label="Menu">
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-[#B87333]/10 bg-[#FBF9F5] px-4 py-4 space-y-1" data-testid="mobile-menu">
          {navLinks.map((l) => (
            <NavLink key={l.to} to={l.to} onClick={() => setOpen(false)}
              className="block py-2.5 text-[#2C2621] font-medium" data-testid={`mobile-nav-${l.to.slice(1)}`}>
              {l.label}
            </NavLink>
          ))}
          <div className="pt-3 space-y-2 border-t border-[#B87333]/10 mt-2">
            <Link to="/book" onClick={() => setOpen(false)} className="block text-center bg-[#B87333] text-white font-medium rounded-full px-5 py-3 shadow-md" data-testid="mobile-book-btn">
              Book Now
            </Link>
            <Link to="/get-estimate" onClick={() => setOpen(false)} className="block text-center bg-[#2C2621] text-white font-medium rounded-full px-5 py-3" data-testid="mobile-estimate-btn">
              Get a Quote
            </Link>
            {user && user.role === "admin" && (
              <Link to="/admin" onClick={() => setOpen(false)} className="block text-center border border-[#B87333]/40 rounded-full px-5 py-3 text-[#2C2621] font-medium">Owner Portal</Link>
            )}
            {user ? (
              <>
                <Link to="/portal" onClick={() => setOpen(false)} className="block text-center border border-[#B87333]/40 rounded-full px-5 py-3 text-[#2C2621] font-medium" data-testid="mobile-portal-btn">My Account</Link>
                <button onClick={doLogout} className="w-full text-center border border-[#B87333]/40 rounded-full px-5 py-3 text-[#2C2621] font-medium" data-testid="mobile-logout-btn">Log Out</button>
              </>
            ) : (
              <Link to="/login" onClick={() => setOpen(false)} className="block text-center border border-[#B87333]/40 rounded-full px-5 py-3 text-[#2C2621] font-medium" data-testid="mobile-login-btn">Client Login</Link>
            )}
            {(social.facebook_url || social.instagram_url) && (
              <div className="flex justify-center gap-3 pt-3" data-testid="mobile-social">
                {social.facebook_url && <a href={social.facebook_url} target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="w-10 h-10 rounded-full bg-[#F5F0E8] flex items-center justify-center text-[#2C2621]" data-testid="mobile-facebook"><Facebook size={18} /></a>}
                {social.instagram_url && <a href={social.instagram_url} target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="w-10 h-10 rounded-full bg-[#F5F0E8] flex items-center justify-center text-[#2C2621]" data-testid="mobile-instagram"><Instagram size={18} /></a>}
              </div>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
